import type { ErrorEvent, EventHint } from '@sentry/nextjs';

import { describeCauseChain } from './error-cause-chain';
import { redactSensitive } from './redaction';

/**
 * Last-chance enrichment + scrubber for every Sentry event.
 *
 * Enrichment first: attach the Vercel deployment identity (commit SHA, deploy
 * URL) so each server issue answers "which shipped version broke?". The server
 * and edge configs pass it in from `@/env`; this module never imports it,
 * because the browser loads it too and must not bundle server env keys. The
 * browser release comes from the Sentry build plugin instead. Browser
 * events get their PostHog link from `registerPostHogSentryLink`, not here,
 * because this module also runs on the server where posthog-js must not load.
 *
 * Every event also gets the original exception's cause chain
 * (`describeCauseChain`), so an error wrapped on its way to the boundary still
 * says what failed underneath, whichever path captured it.
 *
 * Scrubbing second: `reportObservedError` already runs payloads through
 * `redactSensitive` before handing them to Sentry, but several call sites
 * (route error boundaries, realtime hooks, ad-hoc `Sentry.captureException`
 * calls) bypass it. This walks the event payload one last time so any field
 * whose key looks like a secret or binary blob is replaced before leaving
 * the process, regardless of which code path captured the error.
 *
 * Keeps user/email/IP intact for triage. Sentry 11's additional data
 * collection is enabled; this scrubber still removes secret-like keys from
 * error events before they leave the process.
 */
export function redactSentryEvent(
  event: ErrorEvent,
  hint?: EventHint,
  deployment?: SentryDeployment,
): ErrorEvent {
  attachDeploymentContext(event, deployment);
  attachCauseChain(event, hint);
  if (event.extra) {
    event.extra = redactSensitive(event.extra) as typeof event.extra;
  }
  if (event.contexts) {
    event.contexts = redactSensitive(event.contexts) as NonNullable<typeof event.contexts>;
  }
  if (event.request?.headers) {
    event.request.headers = redactSensitive(event.request.headers) as NonNullable<
      typeof event.request.headers
    >;
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) =>
      breadcrumb.data
        ? {
            ...breadcrumb,
            data: redactSensitive(breadcrumb.data) as NonNullable<typeof breadcrumb.data>,
          }
        : breadcrumb,
    );
  }
  return event;
}

/** Best-effort like the deployment context: a failure here must not drop the event. */
function attachCauseChain(event: ErrorEvent, hint?: EventHint): void {
  try {
    const chain = describeCauseChain(hint?.originalException);
    const links = Object.values(chain);
    const saysMoreThanTheMessage =
      links.length > 1 ||
      links.some((link) => Object.keys(link).some((key) => key !== 'message' && key !== 'name'));
    if (!saysMoreThanTheMessage) return;
    event.contexts = { ...event.contexts, cause_chain: chain };
  } catch {
    // The original event still reports.
  }
}

/** The running Vercel deployment, read from server env by the server configs. */
export type SentryDeployment = {
  commitSha?: string | undefined;
  url?: string | undefined;
};

/**
 * Tag the event with the running deployment. Never throws — a failure here
 * must not drop the error it was meant to enrich.
 */
function attachDeploymentContext(event: ErrorEvent, deployment?: SentryDeployment): void {
  try {
    const commit = deployment?.commitSha;
    const url = deployment?.url;
    if (!commit && !url) return;
    event.contexts = {
      ...event.contexts,
      deployment: {
        ...(commit ? { commit_sha: commit } : {}),
        ...(url ? { url } : {}),
      },
    };
    if (commit) {
      event.tags = { ...event.tags, vercel_commit: commit.slice(0, 7) };
    }
  } catch {
    // Enrichment is best-effort; the original event still reports.
  }
}
